/**
 * <brick-localization>
 * Enhances snippets/localization-picker.liquid (a <details> panel inside
 * Shopify's localization form whose options are submit buttons — works
 * without JS).
 * - aria-expanded / aria-controls on the toggle, one picker open at a time
 * - listbox keyboard: Arrow Up/Down, Home/End, Enter/Space selects
 * - search filter (shown when the list is long), diacritics-insensitive
 * - Esc closes and returns focus to the toggle (and does not close a
 *   surrounding <dialog> such as the mobile menu drawer)
 * - outside click / focus leaving closes
 */
(() => {
  if (customElements.get('brick-localization')) return;

  const normalize = (value) =>
    String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .trim();
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  class BrickLocalization extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('[data-loc-details]');
      if (!this.details) return;
      this.summary = this.details.querySelector('summary');
      this.panel = this.details.querySelector('[data-loc-panel]');
      this.list = this.details.querySelector('[data-loc-list]');
      this.search = this.details.querySelector('[data-loc-search]');
      this.empty = this.details.querySelector('[data-loc-empty]');
      this.items = Array.from(this.details.querySelectorAll('[data-loc-item]'));
      this.letters = Array.from(this.details.querySelectorAll('[data-loc-letter]'));
      this.options = this.items.map((item) => item.querySelector('[data-loc-option]'));
      this.items.forEach((item) => {
        item._search = normalize(item.dataset.search);
      });

      const searchWrap = this.details.querySelector('[data-loc-search-wrap]');
      if (searchWrap) searchWrap.hidden = false;

      this.summary.setAttribute('aria-expanded', String(this.details.open));
      this.summary.setAttribute('aria-haspopup', 'listbox');
      if (this.panel && this.panel.id) this.summary.setAttribute('aria-controls', this.panel.id);
      this.resetRoving();

      this.details.addEventListener('toggle', () => this.onToggle());
      this.addEventListener('keydown', (event) => this.onKeydown(event));
      this.addEventListener('focusout', (event) => {
        if (this.details.open && event.relatedTarget && !this.contains(event.relatedTarget)) this.close(false);
      });
      if (this.search) this.search.addEventListener('input', () => this.filter());

      this.details.addEventListener('click', (event) => {
        const button = event.target.closest('button[type="submit"]');
        if (!button) return;
        this.summary.classList.add('is-loading');
        this.setAttribute('aria-busy', 'true');
      });

      this.onDocumentClick = (event) => {
        if (this.details.open && !this.contains(event.target)) this.close(false);
      };
      document.addEventListener('click', this.onDocumentClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
    }

    onToggle() {
      const open = this.details.open;
      this.summary.setAttribute('aria-expanded', String(open));
      if (!open) return;

      // Only one picker open at a time (header + footer can coexist).
      document.querySelectorAll('brick-localization').forEach((picker) => {
        if (picker !== this && picker.details && picker.details.open) picker.close(false);
      });

      const selected = this.selectedOption();
      if (selected && this.list) {
        // .loc__list is position: relative, so offsetTop is list-relative.
        const row = selected.parentElement;
        this.list.scrollTop = Math.max(0, row.offsetTop - (this.list.clientHeight - row.offsetHeight) / 2);
      }
      // Search first on desktop; on touch avoid popping the keyboard.
      if (this.search && finePointer.matches) {
        this.search.focus({ preventScroll: true });
      } else if (selected) {
        selected.focus({ preventScroll: true });
      }
    }

    close(returnFocus = true) {
      if (!this.details.open) return;
      this.details.open = false;
      if (this.search && this.search.value) {
        this.search.value = '';
        this.filter();
      }
      if (returnFocus) this.summary.focus();
    }

    visibleOptions() {
      return this.options.filter((option, index) => !this.items[index].hidden);
    }

    selectedOption() {
      return this.options.find((option) => option.getAttribute('aria-selected') === 'true') || this.visibleOptions()[0];
    }

    resetRoving() {
      const active = this.options.find((o, i) => o.getAttribute('aria-selected') === 'true' && !this.items[i].hidden) || this.visibleOptions()[0];
      this.options.forEach((option) => (option.tabIndex = option === active ? 0 : -1));
    }

    focusOption(option) {
      if (!option) return;
      this.options.forEach((o) => (o.tabIndex = o === option ? 0 : -1));
      option.focus();
      option.scrollIntoView({ block: 'nearest' });
    }

    filter() {
      const query = normalize(this.search.value);
      let shown = 0;
      this.items.forEach((item) => {
        const match = !query || item._search.includes(query);
        item.hidden = !match;
        if (match) shown += 1;
      });
      this.letters.forEach((letter) => {
        const key = letter.dataset.locLetter;
        letter.hidden = !this.items.some((item) => !item.hidden && item.dataset.letter === key);
      });
      if (this.empty) this.empty.hidden = shown > 0;
      this.resetRoving();
    }

    onKeydown(event) {
      if (!this.details.open) return;
      const { key } = event;

      if (key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        if (event.target === this.search && this.search.value) {
          this.search.value = '';
          this.filter();
          return;
        }
        this.close(true);
        return;
      }

      const visible = this.visibleOptions();
      if (!visible.length) {
        if (key === 'Enter' && event.target === this.search) event.preventDefault();
        return;
      }

      if (event.target === this.search) {
        if (key === 'ArrowDown') {
          event.preventDefault();
          this.focusOption(visible[0]);
        } else if (key === 'Enter') {
          // Never submit the first country by accident; only an unambiguous match.
          event.preventDefault();
          if (visible.length === 1) visible[0].click();
          else this.focusOption(visible[0]);
        }
        return;
      }

      const index = visible.indexOf(event.target);
      if (index === -1) return;
      let next = null;
      if (key === 'ArrowDown') next = visible[Math.min(index + 1, visible.length - 1)];
      else if (key === 'ArrowUp') {
        if (index === 0 && this.search) {
          event.preventDefault();
          this.search.focus();
          return;
        }
        next = visible[Math.max(index - 1, 0)];
      } else if (key === 'Home') next = visible[0];
      else if (key === 'End') next = visible[visible.length - 1];
      if (next) {
        event.preventDefault();
        this.focusOption(next);
      }
    }
  }

  customElements.define('brick-localization', BrickLocalization);
})();
